import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Grid, TextField, Paper } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchProgram, updateProgramGoal } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailField from "../../components/common/DetailField";

export default function ProgramGoalEditPage() {
  const { businessDomainId, businessUnitId, programId, goalId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/programs/${programId}`;

  const [originalGoal, setOriginalGoal] = useState(null);
  const [form, setForm] = useState({ plannedValue: "", deadline: "" });

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchProgram(programId);
        const found = (d.goals || [])[Number(goalId)];
        if (found) {
          setOriginalGoal(found);
          setForm({
            plannedValue: found.plannedValue || "",
            deadline: found.deadline ? found.deadline.substring(0, 10) : "",
          });
        }
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, goalId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const updatedGoal = {
      ...originalGoal,
      plannedValue: form.plannedValue,
      deadline: form.deadline ? `${form.deadline}T00:00:00.000Z` : null,
      updatedGoalAt: new Date().toISOString(),
    };
    try {
      await updateProgramGoal(programId, { previous: originalGoal, post: updatedGoal });
      notify.success(t("message.update.success"));
      navigate(`${basePath}/goals/${goalId}`);
    } catch (err) { notify.apiError(err); }
  };

  if (!originalGoal) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("program.goal.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField label={t("program.goal.indicator")} value={originalGoal.indicator?.indicatorName} />
            {originalGoal.indicator && (
              <Paper variant="outlined" sx={{ mt: 1, p: 2, bgcolor: "grey.50" }}>
                {[
                  { label: t("appKpi.unit"),              value: originalGoal.indicator.unit },
                  { label: t("appKpi.measuringMethod"),    value: originalGoal.indicator.measuringMethod },
                  { label: t("appKpi.evaluationStandard"), value: originalGoal.indicator.evaluationStandard },
                  { label: t("appKpi.description"),        value: originalGoal.indicator.description },
                ].filter((row) => row.value).map((row) => (
                  <Box key={row.label} sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>{row.label}:</Typography>
                    <Typography variant="body2">{row.value}</Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </Grid>
          <Grid size={12}>
            <TextField fullWidth required
              label={`${t("program.goal.plannedValue")}${originalGoal.indicator?.unit ? `（${originalGoal.indicator.unit}）` : ""}`}
              name="plannedValue" value={form.plannedValue} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth type="date" label={t("program.goal.deadline")} name="deadline"
              value={form.deadline} onChange={handleChange} slotProps={{ inputLabel: { shrink: true } }} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/programs/${programId}/goals/${goalId}`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
